import React, { useState, useEffect } from 'react';
import { VoiceRecorder } from './VoiceRecorder';
import {
    XMarkIcon,
    PhoneIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    ChatBubbleBottomCenterTextIcon,
    DocumentTextIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import { APIURL } from '../../../configApi/apiConfig';
import axios from '../../../configApi/axiosConfig';
import { ConfirmacionGuardarGestion } from './ConfirmacionGuardarGestion';

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

/** IDs de resultado que corresponden a un Compromiso de Pago */
const IDS_COMPROMISO = [4, 39, 54, 69];

/** Estado limpio del formulario */
const getInitialFormData = (celular = '') => ({
    fecha: new Date().toLocaleDateString('es-EC'),
    telefono: celular,
    dato: '',
    tipoContacto: '',
    descripcion: '',
    fechaPago: '',
    valor: '0.00',
    observacion: '',
    archivo: null
});

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Convierte una fecha "YYYY-MM-DD" a un Date local (evita desfase UTC). */
const crearFechaLocal = (fecha) => {
    const [year, month, day] = fecha.split('-').map(Number);
    return new Date(year, month - 1, day);
};

/** Extrae datos de la respuesta de la API de forma flexible. */
const extractResponseData = (response) => {
    if (Array.isArray(response.data))          return response.data;
    if (Array.isArray(response.data?.data))    return response.data.data;
    if (response.data?.data)                   return [response.data.data];
    return [];
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export function SaveGestion({ isOpen, isInline = false, onClose, onSuccess, estadoGestion, datosCobranza, data }) {
    const [formData, setFormData]           = useState(getInitialFormData(datosCobranza?.Celular));
    const [tipoContactoData, setTipoContacto]   = useState([]);
    const [selectResultadoData, setSelectResultado] = useState([]);
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState('');
    const [success, setSuccess]             = useState('');
    const [showConfirmacion, setShowConfirmacion] = useState(false);
    const [voiceActive, setVoiceActive]     = useState(false);

    // Sincronizar teléfono cuando cambia datosCobranza
    useEffect(() => {
        setFormData((prev) => ({ ...prev, telefono: datosCobranza?.Celular || '' }));
    }, [datosCobranza]);

    // ── Helpers de estado ─────────────────────────────────────────────────
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLimpiar = () => {
        setFormData(getInitialFormData(datosCobranza?.Celular));
        setTipoContacto([]);
        setSelectResultado([]);
        setError('');
        setSuccess('');
    };

    // ── Carga de catálogos en cascada ─────────────────────────────────────
    const fetchTipoContacto = async (idEstadoGestion) => {
        setLoading(true);
        setError('');
        try {
            const token    = localStorage.getItem('token');
            const response = await axios.get(APIURL.SelectTipoContacto(), {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                params:  { idCbo_EstadoGestion: idEstadoGestion }
            });
            setTipoContacto(extractResponseData(response));
        } catch (error) {
            console.error('Error fetching Tipo Contacto:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSelectResultado = async (idTipoContacto) => {
        setLoading(true);
        setError('');
        try {
            const token    = localStorage.getItem('token');
            const response = await axios.get(APIURL.SelectTipoResultado(), {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                params:  { idCbo_EstadosTipocontacto: idTipoContacto }
            });
            setSelectResultado(extractResponseData(response));
        } catch (error) {
            console.error('Error fetching Select Resultado:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── Validaciones ──────────────────────────────────────────────────────
    const validarFormulario = () => {
        const { dato, tipoContacto, descripcion, telefono, observacion, fechaPago, valor } = formData;
        const telefonoLimpio = telefono.replace(/\D/g, '');
        const esCompromiso   = descripcion && IDS_COMPROMISO.includes(parseInt(descripcion));

        if (!dato)                              return '❌ Por favor selecciona un Tipo de Gestión';
        if (!tipoContacto)                      return '❌ Por favor selecciona un Tipo de Contacto';
        if (!descripcion)                       return '❌ Por favor selecciona un Resultado/Descripción';
        if (telefonoLimpio.length !== 10)       return '❌ El teléfono debe tener exactamente 10 dígitos';
        if (!observacion || observacion.trim().length < 10) return '❌ Las notas deben tener mínimo 10 caracteres';

        if (esCompromiso) {
            if (!fechaPago) return '❌ La fecha de pago es obligatoria cuando hay compromiso';
            if (crearFechaLocal(fechaPago) < new Date(new Date().setHours(0, 0, 0, 0)))
                return '❌ La fecha de pago no puede ser menor a la fecha actual';
            if (!valor || parseFloat(valor) <= 0)
                return '❌ El valor debe ser mayor a 0 cuando hay compromiso de pago';
        }

        return null;
    };

    // ── Guardar (2 pasos: validar → confirmar → enviar) ───────────────────
    const handleGuardarGestion = () => {
        const errorMsg = validarFormulario();
        if (errorMsg) { setError(errorMsg); return; }
        setError('');
        setShowConfirmacion(true);
    };

    const handleConfirmarGuardar = async () => {
        const telefonoLimpio = formData.telefono.replace(/\D/g, '');
        const esCompromiso   = IDS_COMPROMISO.includes(parseInt(formData.descripcion));

        setLoading(true);
        setError('');
        setShowConfirmacion(false);

        try {
            const token = localStorage.getItem('token');
            const payload = {
                idCompra:                data?.idCompra || 0,
                idCbo_EstadoGestion:     parseInt(formData.dato),
                idCbo_EstadosTipocontacto: parseInt(formData.tipoContacto) || 0,
                idCbo_ResultadoGestion:  parseInt(formData.descripcion),
                Notas:                   formData.observacion || '',
                telefono:                telefonoLimpio,
                FechaPago:               formData.fechaPago
                    ? new Date(formData.fechaPago).toISOString()
                    : new Date('2000-01-01T00:00:00Z').toISOString(),
                Valor: esCompromiso ? parseFloat(formData.valor) : 0
            };

            const response = await axios.post(APIURL.SaveGestion(), payload, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
            });

            if (response.data?.success || response.status === 200) {
                setSuccess('Gestión guardada exitosamente');
                setTimeout(() => {
                    handleLimpiar();
                    if (onSuccess) onSuccess();
                    else if (onClose) onClose();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Error al guardar gestión:', err.response?.data || err.message);
            setError(err.response?.data?.message || err.message || 'Error al guardar la gestión');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // ── Computed ──────────────────────────────────────────────────────────
    const telefonoDigitos = formData.telefono.replace(/\D/g, '');
    const esCompromiso    = formData.descripcion && IDS_COMPROMISO.includes(parseInt(formData.descripcion));
    const botonDeshabilitado =
        loading ||
        !formData.dato ||
        !formData.tipoContacto ||
        !formData.descripcion ||
        telefonoDigitos.length !== 10 ||
        formData.observacion.trim().length < 10 ||
        (esCompromiso && (!formData.fechaPago || !formData.valor || parseFloat(formData.valor) <= 0));

    // ─────────────────────────────────────────────────────────────────────
    const content = (
        <>
            <div className={`w-full ${!isInline ? 'bg-white rounded-2xl max-w-2xl mx-auto shadow-2xl max-h-[90vh] overflow-y-auto' : 'bg-transparent'}`}>

                {/* ── Header modal ── */}
                {!isInline && (
                    <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900 text-white p-8 flex items-center justify-between shadow-xl overflow-hidden relative">
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl" />
                            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl" />
                        </div>
                        <div className="flex items-center gap-4 relative z-10">
                            <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 shadow-lg">
                                <DocumentTextIcon className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold tracking-tight">Ingresar Gestión</h2>
                                <p className="text-blue-200 text-sm mt-1">Registra una nueva gestión de cobranza</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="relative z-10 p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-colors">
                            <XMarkIcon className="w-6 h-6 text-white" />
                        </button>
                    </div>
                )}

                {/* ── Header inline ── */}
                {isInline && (
                    <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-slate-200 flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-xl">
                            <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800">Ingresar Gestión</h2>
                            <p className="text-xs text-slate-500">Registra una nueva gestión de cobranza</p>
                        </div>
                    </div>
                )}

                {/* ── Cuerpo del formulario ── */}
                <div
                    className={`p-6 md:p-8 space-y-6 ${!isInline ? 'overflow-y-auto' : ''}`}
                    style={!isInline ? { maxHeight: 'calc(90vh - 220px)' } : {}}
                >
                    {/* Mensajes de estado */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start gap-3 animate-slideIn">
                            <div className="bg-red-100 rounded-full p-2 flex-shrink-0">
                                <XMarkIcon className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-red-800">Error</p>
                                <p className="text-xs text-red-700">{error}</p>
                            </div>
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg flex items-start gap-3 animate-slideIn">
                            <div className="bg-green-100 rounded-full p-2 flex-shrink-0">
                                <CheckIcon className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-green-800">Éxito</p>
                                <p className="text-xs text-green-700">{success}</p>
                            </div>
                        </div>
                    )}

                    {/* ── Sección 1: Información Base ── */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border-l-4 border-blue-500 p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-md">1</div>
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Información Base</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Fecha */}
                            <div className="space-y-2.5">
                                <label className="flex items-center text-xs font-bold text-blue-700 uppercase tracking-wide">
                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                    Fecha
                                </label>
                                <input
                                    type="text"
                                    disabled
                                    readOnly
                                    value={formData.fecha}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all text-sm font-medium text-gray-700 cursor-not-allowed"
                                />
                            </div>

                            {/* Teléfono */}
                            <div className="space-y-2.5">
                                <label className="flex items-center justify-between text-xs font-bold text-blue-700 uppercase tracking-wide">
                                    <span className="flex items-center">
                                        <PhoneIcon className="w-4 h-4 mr-2" />
                                        Teléfono (Obligatorio)
                                    </span>
                                    <span className={`text-xs font-bold ${telefonoDigitos.length === 10 ? 'text-green-600' : 'text-red-600'}`}>
                                        {telefonoDigitos.length}/10
                                    </span>
                                </label>
                                <input
                                    type="tel"
                                    value={formData.telefono}
                                    onChange={(e) => handleInputChange('telefono', e.target.value.replace(/\D/g, ''))}
                                    maxLength="10"
                                    placeholder="Ej: 0991234567"
                                    required
                                    className={`w-full px-4 py-3 rounded-xl border-2 bg-white focus:ring-2 focus:outline-none transition-all text-sm font-medium
                                        ${telefonoDigitos.length === 10
                                            ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                                            : 'border-blue-200 focus:border-blue-500 focus:ring-blue-200'
                                        }`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Sección 2: Flujo de Gestión (cascada) ── */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl border-l-4 border-purple-500 p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-md">2</div>
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Flujo de Gestión</h3>
                            <span className="text-xs text-gray-600 ml-auto bg-purple-100 px-3 py-1 rounded-full">Selecciona en orden →</span>
                        </div>

                        <div className="space-y-5">
                            {/* Paso 1: Tipo de Gestión */}
                            <SelectCascada
                                step={1}
                                label="Tipo de Gestión"
                                value={formData.dato}
                                enabled={true}
                                checked={!!formData.dato}
                                onChange={(val) => {
                                    setFormData((prev) => ({ ...prev, dato: val, tipoContacto: '', descripcion: '' }));
                                    if (val) fetchTipoContacto(val);
                                    else { setTipoContacto([]); setSelectResultado([]); }
                                }}
                                placeholder="→ Seleccionar tipo de gestión..."
                                options={estadoGestion || []}
                                optionKey="idCbo_EstadoGestion"
                                optionLabel="Estado"
                            />

                            {/* Paso 2: Tipo de Contacto */}
                            <SelectCascada
                                step={2}
                                label="Tipo de Contacto"
                                value={formData.tipoContacto}
                                enabled={!!formData.dato}
                                checked={!!formData.tipoContacto}
                                onChange={(val) => {
                                    setFormData((prev) => ({ ...prev, tipoContacto: val, descripcion: '' }));
                                    if (val) fetchSelectResultado(val);
                                    else setSelectResultado([]);
                                }}
                                placeholder="→ Selecciona primero tipo de gestión..."
                                options={tipoContactoData}
                                optionKey="idCbo_EstadosTipocontacto"
                                optionLabel={(item) => item.DescripcionTipoContacto || item.Nombre || item.Estado}
                            />

                            {/* Paso 3: Resultado */}
                            <SelectCascada
                                step={3}
                                label="Resultado/Descripción"
                                value={formData.descripcion}
                                enabled={!!formData.tipoContacto}
                                checked={!!formData.descripcion}
                                onChange={(val) => {
                                    handleInputChange('descripcion', val);
                                    if (!IDS_COMPROMISO.includes(parseInt(val))) {
                                        setFormData((prev) => ({ ...prev, descripcion: val, fechaPago: '', valor: '0.00' }));
                                    }
                                }}
                                placeholder="→ Selecciona primero tipo de contacto..."
                                options={selectResultadoData}
                                optionKey="idCbo_ResultadoGestion"
                                optionLabel={(item) => item.Resultado || item.Descripcion}
                            />
                        </div>
                    </div>

                    {/* ── Sección 3: Compromiso de Pago (condicional) ── */}
                    {esCompromiso && (
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl border-l-4 border-amber-500 p-6 shadow-sm hover:shadow-md transition-all animate-fadeIn">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-md">3</div>
                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Detalles de Compromiso de Pago</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Fecha de pago */}
                                <div className="space-y-2.5">
                                    <label className="text-xs font-bold text-amber-700 uppercase tracking-wide flex items-center">
                                        <CalendarIcon className="w-4 h-4 mr-2" />
                                        Fecha de Pago Comprometida (Obligatoria)
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.fechaPago}
                                        onChange={(e) => handleInputChange('fechaPago', e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:outline-none transition-all text-sm bg-white font-medium cursor-pointer
                                            ${formData.fechaPago && crearFechaLocal(formData.fechaPago) >= new Date(new Date().setHours(0, 0, 0, 0))
                                                ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                                                : 'border-amber-200 focus:border-amber-500 focus:ring-amber-200'
                                            }`}
                                    />
                                    {formData.fechaPago && crearFechaLocal(formData.fechaPago) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                                        <p className="text-xs text-red-600 font-bold">❌ La fecha no puede ser menor a hoy</p>
                                    )}
                                </div>
                                {/* Monto */}
                                <div className="space-y-2.5">
                                    <label className="text-xs font-bold text-amber-700 uppercase tracking-wide flex items-center">
                                        <CurrencyDollarIcon className="w-4 h-4 mr-2" />
                                        Monto a Pagar (Obligatorio y Mayor a 0)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.valor}
                                        onChange={(e) => handleInputChange('valor', e.target.value)}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:outline-none transition-all text-sm bg-white font-medium
                                            ${formData.valor && parseFloat(formData.valor) > 0
                                                ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                                                : 'border-amber-200 focus:border-amber-500 focus:ring-amber-200'
                                            }`}
                                    />
                                    {formData.valor && parseFloat(formData.valor) <= 0 && (
                                        <p className="text-xs text-red-600 font-bold">❌ El monto debe ser mayor a 0</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Sección 4: Observaciones ── */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl border-l-4 border-green-500 p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-md">4</div>
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Notas Adicionales</h3>
                        </div>
                        <label className="flex items-center justify-between text-xs font-bold text-green-700 uppercase tracking-wide mb-3">
                            <span className="flex items-center">
                                <ChatBubbleBottomCenterTextIcon className="w-4 h-4 mr-2" />
                                Observación (Mínimo 10 caracteres)
                            </span>
                            <span className={`text-xs font-bold ${formData.observacion.trim().length >= 10 ? 'text-green-600' : 'text-red-600'}`}>
                                {formData.observacion.trim().length}/10
                            </span>
                        </label>
                        <div className={`transition-all duration-300 rounded-xl ${voiceActive ? 'ring-2 ring-red-400 ring-offset-2 shadow-lg shadow-red-100' : ''}`}>
                            <textarea
                                value={formData.observacion}
                                onChange={(e) => handleInputChange('observacion', e.target.value.replace(/[^a-zA-Z0-9 áéíóúÁÉÍÓÚñÑ.,;:()\-\n\r]/g, ''))}
                                rows="4"
                                aria-label="Campo de observaciones"
                                placeholder="Ingrese observaciones, notas especiales o detalles adicionales (mínimo 10 caracteres, sin caracteres especiales)..."
                                className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-2 focus:outline-none transition-all text-sm resize-none bg-white font-medium uppercase
                                    ${formData.observacion.trim().length >= 10
                                        ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                                        : 'border-green-200 focus:border-green-500 focus:ring-green-200'
                                    }`}
                            />
                        </div>
                        <VoiceRecorder
                            onTranscript={(text) => {
                                setFormData((prev) => {
                                    const sep = prev.observacion.length > 0 && !prev.observacion.endsWith(' ') ? ' ' : '';
                                    return { ...prev, observacion: prev.observacion + sep + text };
                                });
                            }}
                            onActiveChange={setVoiceActive}
                        />
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className={`sticky bottom-0 border-t flex justify-end gap-3 ${!isInline
                    ? 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-slate-700 p-6 shadow-2xl'
                    : 'bg-slate-50 border-slate-200 px-6 py-4'}`}
                >
                    <button
                        onClick={() => { handleLimpiar(); onClose(); }}
                        className={`px-5 py-2.5 font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 transform hover:scale-105
                            ${!isInline
                                ? 'bg-slate-700 hover:bg-slate-600 text-white shadow-lg'
                                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 shadow-sm'}`}
                    >
                        <XMarkIcon className="w-5 h-5" />
                        Cancelar
                    </button>
                    <button
                        onClick={handleGuardarGestion}
                        disabled={botonDeshabilitado}
                        className={`px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700
                            disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-xl transition-all duration-200
                            flex items-center gap-2 transform hover:scale-105 disabled:opacity-50 disabled:scale-100
                            ${!isInline ? 'shadow-lg' : 'shadow-sm'}`}
                    >
                        <CheckIcon className="w-5 h-5" />
                        {loading ? 'Guardando...' : 'Guardar Gestión'}
                    </button>
                </div>
            </div>

            <ConfirmacionGuardarGestion
                isOpen={showConfirmacion}
                onConfirm={handleConfirmarGuardar}
                onCancel={() => setShowConfirmacion(false)}
                isCompromiso={IDS_COMPROMISO.includes(Number(formData.dato))}
                formData={formData}
                datosCobranza={datosCobranza}
                IDS_COMPROMISO={IDS_COMPROMISO}
            />
        </>
    );

    return isInline ? content : (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fadeIn">
            {content}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: Select en cascada
// ─────────────────────────────────────────────────────────────────────────────
function SelectCascada({ step, label, value, enabled, checked, onChange, placeholder, options, optionKey, optionLabel }) {
    const color = enabled ? '#9333ea' : '#9ca3af';
    const bgBadge = enabled ? '#e9d5ff' : '#e5e7eb';

    return (
        <div className="space-y-2.5 transition-all duration-300">
            <label className="text-xs font-bold uppercase tracking-wide flex items-center" style={{ color }}>
                <span
                    className="rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 transition-all"
                    style={{ backgroundColor: bgBadge, color }}
                >
                    {step}
                </span>
                {label}
                {checked && (
                    <span className="ml-auto text-green-600 text-xs font-bold flex items-center gap-1">
                        <CheckIcon className="w-4 h-4" /> Seleccionado
                    </span>
                )}
            </label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={!enabled}
                className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all text-sm bg-white font-medium"
                style={{
                    borderColor:     enabled ? '#e9d5ff' : '#f3f4f6',
                    backgroundColor: enabled ? '#ffffff' : '#f9fafb',
                    cursor:          enabled ? 'pointer' : 'not-allowed',
                    opacity:         enabled ? 1 : 0.6
                }}
            >
                <option value="">{placeholder}</option>
                {options.map((item) => (
                    <option key={item[optionKey]} value={item[optionKey]}>
                        {typeof optionLabel === 'function' ? optionLabel(item) : item[optionLabel]}
                    </option>
                ))}
            </select>
        </div>
    );
}
